import React, { createContext, useContext, useState } from 'react';
import { User, UserRole } from '../types/user';
import { UserFormData } from '../services/userService';
import userService from '../services/userService';

interface UserContextType {
  users: User[];
  loading: boolean;
  error: string | null;
  fetchUsers: () => Promise<void>;
  createUser: (userData: UserFormData) => Promise<User>;
  updateUser: (id: string, userData: Partial<UserFormData>) => Promise<User>;
  deleteUser: (id: string) => Promise<void>;
  getUsersByRole: (role: UserRole) => Promise<User[]>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке пользователей');
      console.error('Ошибка при загрузке пользователей:', err);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: UserFormData) => {
    try {
      const newUser = await userService.createUser(userData);
      await fetchUsers();
      return newUser;
    } catch (err) {
      console.error('Ошибка при создании пользователя:', err);
      throw err;
    }
  };

  const updateUser = async (id: string, userData: Partial<UserFormData>) => {
    try {
      const updatedUser = await userService.updateUser(id, userData);
      await fetchUsers();
      return updatedUser;
    } catch (err) {
      console.error('Ошибка при обновлении пользователя:', err);
      throw err;
    }
  };

  const deleteUser = async (id: string) => {
    try {
      await userService.deleteUser(id);
      await fetchUsers();
    } catch (err) {
      console.error('Ошибка при удалении пользователя:', err);
      throw err;
    }
  };

  const getUsersByRole = async (role: UserRole) => {
    try {
      return await userService.getUsersByRole(role);
    } catch (err) {
      console.error('Ошибка при получении пользователей по роли:', err);
      throw err;
    }
  };

  return (
    <UserContext.Provider
      value={{
        users,
        loading,
        error,
        fetchUsers,
        createUser,
        updateUser,
        deleteUser,
        getUsersByRole
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUsers = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUsers must be used within a UserProvider');
  }
  return context;
}; 